import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ReactSVG } from 'react-svg'

import useStoriesModal from '@entities/Stories/hooks/useStoriesModal'
import { useStoriesStore } from '@entities/Stories/model'

import { useAllMarketingStoriesQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import ButtonClose from '@shared/ui/button-close'
import PlayControl from '@shared/ui/play-control'

import styles from './styles.module.scss'

interface ModalStoriesProps {
  showStartId: string;
}

const TIMER = 10000

export default function ModalStories ({ showStartId }: ModalStoriesProps) {
  const { data } = useAllMarketingStoriesQuery({
    variables: {
      imageStyle2: '600xauto',
    },
  })

  const [ activeId, setActiveId ] = useState<string>(showStartId)
  const [ slideIndex, setSlideIndex ] = useState<number>(0)
  const [ isPaused, setIsPaused ] = useState<boolean>(false)
  const { markAsWatched } = useStoriesStore()
  const { closeStoriesModal } = useStoriesModal()
  const groups = useMemo(() => data?.allMarketingStories?.edges || [], [ data?.allMarketingStories?.edges ])

  const timerIdRef = useRef<NodeJS.Timeout | null>(null)
  const remainingTimeRef = useRef<number>(TIMER)
  const startTimeRef = useRef<number>(0)

  const currentGroupIndex = useMemo(() => {
    return groups.findIndex((g) => g?.id === activeId)
  }, [ groups, activeId ])

  const isLastGroup = currentGroupIndex === groups.length - 1
  const ACTIVE_GROUP = groups[currentGroupIndex]
  const slides = useMemo(() => ACTIVE_GROUP?.slides || [], [ ACTIVE_GROUP ])
  const isLastSlide = slideIndex === slides.length - 1

  const canGoPrev = slideIndex > 0 || currentGroupIndex > 0
  const canGoNext = !isLastSlide || !isLastGroup

  useEffect(() => {
    remainingTimeRef.current = TIMER
  }, [ slideIndex, activeId ])

  useEffect(() => {
    if (isPaused || !slides.length) return

    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current)
      timerIdRef.current = null
    }

    startTimeRef.current = Date.now()

    timerIdRef.current = setTimeout(() => {
      if (!isLastSlide) {
        setSlideIndex((prev) => prev + 1)
      }
      else if (!isLastGroup) {
        const nextGroup = groups[currentGroupIndex + 1]
        if (nextGroup) {
          setActiveId(nextGroup.id as string)
          setSlideIndex(0)
        }
      }
      else {
        closeStoriesModal()
      }
    }, remainingTimeRef.current)

    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current)
        timerIdRef.current = null
      }
    }
  }, [
    isPaused,
    slideIndex,
    slides,
    groups,
    currentGroupIndex,
    isLastSlide,
    isLastGroup,
    closeStoriesModal,
  ])

  const handleTogglePause = () => {
    if (!isPaused) {
      const elapsed = Date.now() - startTimeRef.current
      remainingTimeRef.current -= elapsed

      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current)
        timerIdRef.current = null
      }
    }
    setIsPaused((prev) => !prev)
  }

  const handleNext = () => {
    if (!isLastSlide) {
      setSlideIndex((prev) => prev + 1)
    }
    else if (!isLastGroup) {
      const nextGroup = groups[currentGroupIndex + 1]
      if (nextGroup) {
        setActiveId(nextGroup.id as string)
        setSlideIndex(0)
      }
    }
    else {
      closeStoriesModal()
    }
  }

  const handlePrev = () => {
    if (slideIndex > 0) {
      setSlideIndex((prev) => prev - 1)
    }
    else if (currentGroupIndex > 0) {
      const prevGroup = groups[currentGroupIndex - 1]
      if (prevGroup) {
        setActiveId(prevGroup.id as string)
        const prevSlides = prevGroup.slides || []
        setSlideIndex(prevSlides.length - 1)
      }
    }
  }

  useEffect(() => {
    if (activeId) {
      markAsWatched(activeId)
    }
  }, [ activeId, markAsWatched ])

  return (
    <div className={styles.root}>
      <div className={styles.root__top}>
        <div className={styles.root__pagination}>
          {slides.map((_, index) => (
            <div
              key={index}
              className={cn(
                styles.root__bar,
                index < slideIndex && styles.root__bar__filled,
                index === slideIndex && styles.root__bar__active,
              )}
            >
              {index === slideIndex && (
                <div
                  className={styles.root__bar__progress}
                  style={{
                    animationDuration: `${TIMER}ms`,
                    animationPlayState: isPaused ? 'paused' : 'running',
                  }}
                />
              )}
            </div>
          ))}
        </div>
        <ButtonClose
          className={styles.root__close}
          onClick={closeStoriesModal}
        />
      </div>

      <div className={styles.root__content}>
        {slides?.[slideIndex] && (
          <img
            className={styles.root__slide}
            src={slides[slideIndex]?.image?.url || ''}
            alt=""
            fetchPriority="low"
            loading="lazy"
          />
        )}

        <PlayControl
          className={styles.root__control}
          isPause={!isPaused}
          onClick={handleTogglePause}
        />

        <div className={styles.root__arrows}>
          {canGoPrev && (
            <button
              className={cn('reset-button', styles.root__prev)}
              onClick={handlePrev}
            >
              <ReactSVG src="/icons/arrow.svg" />
            </button>
          )}

          {canGoNext && (
            <button
              className={cn('reset-button', styles.root__next)}
              onClick={handleNext}
            >
              <ReactSVG src="/icons/arrow.svg" />
            </button>
          )}
        </div>

        {slides?.[slideIndex]?.optionOpenInBrowser && (
          <Link
            className={styles.root__link}
            to={slides?.[slideIndex]?.url as string}
          />
        )}

        {slides?.[slideIndex]?.optionOpenInNewWindow && (
          <a
            className={styles.root__link}
            href={slides?.[slideIndex]?.url as string}
            target="_blank"
          />
        )}
      </div>
    </div>
  )
}
